import { Spacing } from '@persona-id/design-tokens';
import { fromPairs } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { WorkflowError, WorkflowErrorType } from 'dashboard/models/workflow';

export const WorkflowErrorMessage = styled('div')<{ narrow?: boolean }>`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  margin-top: ${({ narrow }) => (narrow ? 0 : `${Spacing[2]}`)};
`;

const useWorkflowError = <FieldKeys extends string>(errors: WorkflowError[]) => {
  const firstErrorMessagePerField = useMemo(() => {
    return errors.reduce<Record<string, { message: string }>>((firstErrors, error) => {
      if (error.type === WorkflowErrorType.Run) {
        return {
          ...(error.fields
            ? fromPairs(error.fields.map((field) => [field, { message: error.message }]))
            : {}),
          ...firstErrors,
        };
      } else {
        return {
          ...(error.field ? { [error.field]: { message: error.message } } : {}),
          ...firstErrors,
        };
      }
    }, {});
  }, [errors]);

  const hasErrorFor = useCallback(
    (field: FieldKeys) => Object.keys(firstErrorMessagePerField).includes(field),
    [firstErrorMessagePerField],
  );

  const renderErrorMessageFor = useCallback(
    (fieldOrFields: FieldKeys | FieldKeys[], options: { narrow?: boolean } = {}) => {
      const fields = Array.isArray(fieldOrFields) ? fieldOrFields : [fieldOrFields];

      const erroredField = fields.find((field) => firstErrorMessagePerField[field]);
      return erroredField ? (
        <WorkflowErrorMessage narrow={options.narrow}>
          {firstErrorMessagePerField[erroredField].message}
        </WorkflowErrorMessage>
      ) : (
        <></>
      );
    },
    [firstErrorMessagePerField],
  );

  return {
    renderErrorMessageFor,
    hasErrorFor,
  };
};

export default useWorkflowError;
